import { useEffect, useState } from 'react';
import { login as doLogin } from '../auth/auth';

export default function Login() {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const devPrefill = host === 'localhost' || host === '127.0.0.1' || host.startsWith('192.168.');
  const [organization, setOrganization] = useState(devPrefill ? 'SUPER' : '');
  const [username, setUsername] = useState(devPrefill ? 'admin' : '');
  const [password, setPassword] = useState(devPrefill ? 'admin' : '');
  
  // 2FA State
  const [step, setStep] = useState<'credentials' | '2fa_code' | '2fa_email_sent'>('credentials');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorEmail, setTwoFactorEmail] = useState('');
  const [twoFactorMessage, setTwoFactorMessage] = useState('');
  
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [connectOk, setConnectOk] = useState<boolean>(false);
  const [maintenance, setMaintenance] = useState<{ enabled: boolean; message: string | null } | null>(null);

  async function fetchMaintenance() {
    try {
      const r = await fetch('/api/system/maintenance');
      if (r.ok) {
        const data = await r.json();
        setMaintenance({ enabled: !!data?.enabled, message: data?.message ?? null });
        return data;
      }
    } catch { /* ignore */ }
    return null;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const codeToSend = step === '2fa_code' ? twoFactorCode.trim() : undefined;
      const res = await doLogin(organization.trim(), username.trim(), password, codeToSend);

      if (res?.requires_2fa) {
        if (res.setup_required) {
          setStep('2fa_email_sent');
          setTwoFactorEmail(res.email || 'Ihre E-Mail');
          setTwoFactorMessage(res.message || 'Ein Einrichtungslink wurde an Ihre E-Mail gesendet.');
        } else {
          setStep('2fa_code');
          setTwoFactorMessage(res.message || 'Bitte 6-stelligen Code eingeben.');
        }
        return;
      }

      // Erfolgreicher Login -> Weiterleitung
      try {
        sessionStorage.removeItem('theme_effect_disabled');
      } catch {}
      window.location.href = '/';
    } catch (err: any) {
      const msg = err && typeof err === 'object' && 'message' in err ? (err as any).message : String(err);
      if (typeof msg === 'string' && msg.includes('MAINTENANCE_MODE')) {
        const m = await fetchMaintenance();
        setError(
          (m && m.message) ||
          'Das System befindet sich aktuell im Wartungsmodus. Bitte versuchen Sie es später erneut.'
        );
      } else {
        setError(msg || 'Login fehlgeschlagen');
      }
    } finally {
      setLoading(false);
    }
  }

  const handleBackToCredentials = () => {
    setStep('credentials');
    setTwoFactorCode('');
    setError(null);
  };

  useEffect(() => {
    fetchMaintenance();
    (async () => {
      try {
        const res = await fetch('/api/reports-dev/stock', { method: 'GET' });
        if (res.ok) {
          setConnectOk(true);
        } else {
          setConnectOk(false);
        }
      } catch {
        setConnectOk(false);
      }
    })();
  }, []);

  return (
    <div className="login-wrapper px-4">
      <div className={`login-card max-w-md mx-auto ${connectOk === true ? 'border-2 border-green-500' : connectOk === false ? 'border-2 border-red-500' : ''}`}>
        {/* OrgaPilot Logo */}
        <div style={{ textAlign: 'center', marginBottom: 8, display: 'flex', justifyContent: 'center' }}>
          <img src="/VereinsOS_Patch.png" alt="OrgaPilot Logo" style={{ maxWidth: '100%', width: '450px', height: 'auto' }} />
        </div>

        <h1 className="text-4xl md:text-5xl" style={{ fontWeight: 'bold', background: 'linear-gradient(135deg, #FF6B35 0%, #4A5568 25%, #48BB78 50%, #4299E1 75%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', lineHeight: '1.3', paddingBottom: '4px' }}>
          OrgaPilot
        </h1>

        {maintenance?.enabled && (
          <div style={{ marginBottom: 12, padding: '10px 12px', border: '2px solid #dc2626', background: '#fef2f2', color: '#7f1d1d', borderRadius: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 'bold', marginBottom: 4 }}>
              <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: '#dc2626' }} />
              Wartungsmodus aktiv
            </div>
            <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>
              {maintenance.message || 'Das System wird gerade gewartet. Nur SUPER und TESTORG können sich anmelden.'}
            </div>
          </div>
        )}

        {error && <p style={{ color: '#ef4444', marginBottom: 12, fontSize: 13, fontWeight: '600' }}>⚠️ {error}</p>}

        {step === '2fa_email_sent' ? (
          <div className="space-y-4 py-2 text-left">
            <div className="p-4 bg-blue-50 dark:bg-blue-950/50 border-2 border-blue-300 dark:border-blue-800 rounded-xl space-y-2">
              <div className="font-bold text-blue-900 dark:text-blue-100 flex items-center gap-2 text-base">
                <span>🛡️</span> 2FA-Ersteinrichtung erforderlich
              </div>
              <p className="text-xs text-blue-800 dark:text-blue-200 leading-relaxed">
                Für Ihre Organisation bzw. Ihren Benutzeraccount ist die <strong>Zwei-Faktor-Authentifizierung (2FA)</strong> aktiviert.
              </p>
              <div className="p-2.5 bg-white dark:bg-gray-800 rounded-lg border border-blue-200 dark:border-blue-800 text-xs font-mono text-slate-800 dark:text-slate-200">
                ✉️ Link gesendet an: <strong>{twoFactorEmail}</strong>
              </div>
              <p className="text-[11px] text-slate-600 dark:text-slate-400">
                Bitte öffnen Sie Ihr E-Mail-Postfach und klicken Sie auf den Bestätigungslink, um den QR-Code mit Ihrer Authenticator-App zu scannen.
              </p>
            </div>

            <button
              type="button"
              onClick={handleBackToCredentials}
              className="w-full py-2.5 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 text-gray-800 dark:text-gray-100bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-100 font-semibold rounded-lg text-xs transition"
            >
              &larr; Zurück zur Anmeldung
            </button>
          </div>
        ) : step === '2fa_code' ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="p-3 bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800/80 rounded-xl text-left">
              <div className="text-xs font-bold text-blue-900 dark:text-blue-200 flex items-center gap-1.5 mb-1">
                <span>🛡️</span> Zwei-Faktor-Code erforderlich
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-300">
                Geben Sie den aktuellen 6-stelligen Sicherheitscode aus Ihrer Authenticator-App für <strong>{username}</strong> ({organization}) ein:
              </p>
            </div>

            <div>
              <label>6-stelliger 2FA-Code</label>
              <input
                type="text"
                maxLength={6}
                value={twoFactorCode}
                onChange={e => setTwoFactorCode(e.target.value.replace(/\D/g, ''))}
                placeholder="123456"
                autoFocus
                style={{ textAlign: 'center', letterSpacing: '6px', fontSize: '20px', fontWeight: 'bold' }}
                required
              />
            </div>

            <button type="submit" disabled={loading || twoFactorCode.length !== 6}>
              {loading ? 'Prüfe Code...' : '✓ Anmelden'}
            </button>

            <button
              type="button"
              onClick={handleBackToCredentials}
              style={{ background: 'transparent', color: '#666', border: 'none', cursor: 'pointer', fontSize: '12px', marginTop: '8px' }}
            >
              &larr; Zurück / Anderer Benutzer
            </button>
          </form>
        ) : (
          <form onSubmit={handleSubmit}>
            <label>Organisation</label>
            <input
              value={organization}
              onChange={e => setOrganization(e.target.value)}
              autoComplete="organization"
              required
            />

            <label>Benutzer</label>
            <input
              value={username}
              onChange={e => setUsername(e.target.value)}
              autoComplete="username"
              required
            />

            <label>Passwort</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />

            <button type="submit" disabled={loading}>
              {loading ? 'Anmelden...' : 'Anmelden'}
            </button>
          </form>
        )}

        <div style={{ marginTop: '20px', textAlign: 'center', fontSize: '12px', color: '#666' }}>
          <div style={{ marginBottom: '4px' }}>OrgaPilot V26.10.7</div>
          <div>Copyright 2026 by Tim Jeschonowski</div>
        </div>
      </div>
    </div>
  );
}